import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useIntl } from 'react-intl';

import { formatMoney } from '../../../util/currency';
import { nightsBetween } from '../../../util/dates';
import { FormattedMessage } from '../../../util/reactIntl';
import {
  COMMISSION_LINE_ITEMS,
  EXTRA_LINE_ITEMS,
  LINE_ITEM_LISTING_CONTINGENT,
  LINE_ITEM_NIGHT,
  NEGATIVE_LINE_ITEMS,
} from '../../../util/types';
import { itemValueStyles, lineItemStyles, totalDividerStyles } from './BookingBreakdown.styles';

const createExtraLineItemLabel = (item, intl, quantity, seats, units, nights, extraLineItems) => {
  const unitPrice = formatMoney(intl, item.unitPrice);

  // if the line item has only quantity
  if (quantity && !seats && !units) {
    return intl.formatMessage(
      { id: `BookingBreakdown.quantity.${item.code.replace('line-item/', '')}` },
      { unitPrice, quantity }
    );
  }

  // for extra guests, extra adults or extra children
  if (item.code.includes('extra')) {
    return intl.formatMessage(
      { id: `BookingBreakdown.${item.code.replace('line-item/', '')}` },
      { unitPrice, seats, units }
    );
  }

  // for extra line items that are in the metadata
  const extraLineItem = extraLineItems?.find((extraLineItem) => extraLineItem.code === item.code);

  if (!extraLineItem) return '';

  const itemLabel = intl.formatMessage(
    { id: `BookingBreakdown.${item.code.replace('line-item/', '')}` },
    { unitPrice }
  );
  const guestCountLabel = extraLineItem?.type?.perGuest
    ? `${intl.formatMessage({ id: `BookingBreakdown.guestCount` }, { count: item.seats })}`
    : '';
  const nightCountLabel = extraLineItem?.type?.perNight
    ? `${intl.formatMessage({ id: `BookingBreakdown.nightCount` }, { count: nights })}`
    : '';
  if (guestCountLabel && !nightCountLabel) {
    return `${itemLabel} * ${guestCountLabel}`;
  } else if (!guestCountLabel && nightCountLabel) {
    return `${itemLabel} * ${nightCountLabel}`;
  } else {
    return `${itemLabel} * ${guestCountLabel} * ${nightCountLabel}`;
  }
};

/**
 * @param {object} props - component props
 * @param {object} props.transaction - transaction object
 * @param {'provider' | 'customer'} props.userRole - user role
 */

const LineItemKnownRefundMaybe = ({ transaction, userRole }) => {
  const intl = useIntl();

  let items =
    userRole === 'provider'
      ? transaction.attributes.lineItems.filter((item) => item.includeFor.includes('provider'))
      : transaction.attributes.lineItems.filter((item) => item.includeFor.includes('customer'));

  // Remove the night line item if it's not a refund
  items = items.filter((item) => item.code !== LINE_ITEM_NIGHT && item.lineTotal.amount < 0);

  // Remove vouchers as they are negative
  items = items.filter((item) => !NEGATIVE_LINE_ITEMS.includes(item.code));

  // Remove all comission line items
  items = items.filter((item) => !COMMISSION_LINE_ITEMS.includes(item.code));

  // Remove listing contingent line items
  items = items.filter((item) => item.code !== LINE_ITEM_LISTING_CONTINGENT);

  // Remove unknown line items
  items = items.filter((item) => EXTRA_LINE_ITEMS.includes(item.code));

  const { start, end } = transaction.booking.attributes;
  const nights = nightsBetween(start, end);
  const extraLineItems = transaction.attributes.metadata.extraLineItems || [];

  if (items.length === 0) return null;

  const renderExtraLineItems = (item, index) => {
    const quantity = item.quantity;
    const seats = item.seats;
    const units = item.units;

    const label = createExtraLineItemLabel(
      item,
      intl,
      quantity,
      seats,
      units,
      nights,
      extraLineItems
    );

    const formattedTotal = formatMoney(intl, item.lineTotal);
    const refundLabel = intl.formatMessage({ id: 'BookingBreakdown.refund' });

    return (
      <Box key={`${index}-item.code`} sx={lineItemStyles}>
        <Typography sx={{ flex: 1 }} variant="lineItemLabel">{label + ' ' + refundLabel}</Typography>
        <Typography sx={itemValueStyles} variant="lineItemLabel">{formattedTotal}</Typography>
      </Box>
    );
  };

  return (
    <>
      <Divider sx={totalDividerStyles} />
      <Typography fontWeight="bold" variant="lineItemLabel">
        <FormattedMessage id="BookingBreakdown.extraCost.refund" />
      </Typography>
      {items.map((item, i) => renderExtraLineItems(item, i))}
    </>
  );
};

export default LineItemKnownRefundMaybe;
